import { useCallback, useEffect, useState } from 'react';
import { adminRequest, normalizeError } from '../api/adminApi';

const EMPTY_FORM = {
  code: '',
  productIdsText: '',
  startAt: '',
  expireAt: '',
  enabled: true,
};

function toPayload(form) {
  return {
    code: form.code,
    productIds: form.productIdsText
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    startAt: new Date(form.startAt).toISOString(),
    expireAt: new Date(form.expireAt).toISOString(),
    enabled: form.enabled,
  };
}

function fromIsoToLocalInput(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60_000;
  const localDate = new Date(date.getTime() - offsetMs);
  return localDate.toISOString().slice(0, 16);
}

function PromoCodesView({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminRequest(token, '/api/admin/promo-code', { onUnauthorized });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(normalizeError(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    try {
      const endpoint = editId ? `/api/admin/promo-code/${editId}` : '/api/admin/promo-code';
      const method = editId ? 'PUT' : 'POST';
      await adminRequest(token, endpoint, {
        method,
        body: JSON.stringify(toPayload(form)),
        onUnauthorized,
      });
      setForm(EMPTY_FORM);
      setEditId(null);
      await loadItems();
    } catch (e) {
      setError(normalizeError(e));
    }
  };

  const beginEdit = (item) => {
    setEditId(item.id);
    setForm({
      code: item.code ?? '',
      productIdsText: Array.isArray(item.productIds) ? item.productIds.join(', ') : '',
      startAt: fromIsoToLocalInput(item.startAt),
      expireAt: fromIsoToLocalInput(item.expireAt),
      enabled: Boolean(item.enabled),
    });
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete promo code?')) return;
    setError(null);
    try {
      await adminRequest(token, `/api/admin/promo-code/${id}`, {
        method: 'DELETE',
        onUnauthorized,
      });
      await loadItems();
    } catch (e) {
      setError(normalizeError(e));
    }
  };

  return (
    <div className="entities-scroller">
      <form className="entity-form" onSubmit={submit}>
        <h3>{editId ? 'Edit promo code' : 'Create promo code'}</h3>
        <label>
          Code
          <input
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            required
          />
        </label>
        <label>
          Product IDs (comma separated)
          <input
            value={form.productIdsText}
            onChange={(e) => setForm((prev) => ({ ...prev, productIdsText: e.target.value }))}
          />
        </label>
        <label>
          Start at
          <input
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => setForm((prev) => ({ ...prev, startAt: e.target.value }))}
            required
          />
        </label>
        <label>
          Expire at
          <input
            type="datetime-local"
            value={form.expireAt}
            onChange={(e) => setForm((prev) => ({ ...prev, expireAt: e.target.value }))}
            required
          />
        </label>
        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
          />
          Enabled
        </label>
        <div className="entity-form-actions">
          <button type="submit">{editId ? 'Save' : 'Create'}</button>
          {editId && (
            <button type="button" onClick={() => { setEditId(null); setForm(EMPTY_FORM); }}>
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {loading && <p className="auth-hint">Loading…</p>}
      {error && <p className="auth-error">{error}</p>}
      {!loading && !error && (
        <table className="entities-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Products</th>
              <th>Start</th>
              <th>Expire</th>
              <th>Enabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.code}</td>
                <td>{Array.isArray(item.productIds) ? item.productIds.join(', ') : '—'}</td>
                <td>{item.startAt}</td>
                <td>{item.expireAt}</td>
                <td>{item.enabled ? 'Yes' : 'No'}</td>
                <td className="actions-cell">
                  <button type="button" onClick={() => beginEdit(item)}>Edit</button>
                  <button type="button" onClick={() => removeItem(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-cell">No promo codes found.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default PromoCodesView;
